import React, { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { getCandidateTagHistory } from 'app/services/CandidateServices/CandidateServices';
import { buildTagChangeHistoryRows } from './tagTypeHelpers';
import { httpSuccess } from 'app/services/serviceHelpers';
import { ICandidateTagHistory } from 'app/models/Candidate/Tags';

const useStyles = makeStyles()(() => ({
  paperMd: {
    width: '100%',
    minWidth: '720px',
    outline: 'none',
  },
  tableWrap: {
    marginTop: '8px',
    fontSize: 12,
  },
  tableContainer: {
    padding: '24px',
  },
  tableHead: {
    backgroundColor: '#fff',
    fontWeight: 'bold',
  },
  tableCell: {
    fontSize: 14,
    verticalAlign: 'top',
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: '15px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  emptyState: {
    padding: '24px',
    color: '#707070',
  },
}));

type CandidateTagHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  candidateId?: number;
  brandId?: number;
};

export const CandidateTagHistoryModal = ({ open, onClose, candidateId, brandId }: CandidateTagHistoryModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [historyRows, setHistoryRows] = useState<ICandidateTagHistory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !candidateId || !brandId) {
      return;
    }

    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await getCandidateTagHistory(candidateId, brandId);
        if (httpSuccess(response?.status) && Array.isArray(response?.data)) {
          setHistoryRows(response.data);
        } else {
          setHistoryRows([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [open, candidateId, brandId]);

  const historyColumns = useMemo(
    () => [
      { title: 'changedDate' as const, value: t('candidate.candidateHeader.tagHistoryChangedDate') },
      { title: 'changedBy' as const, value: t('candidate.candidateHeader.tagHistoryChangedBy') },
      { title: 'oldValue' as const, value: t('candidate.candidateHeader.tagHistoryOldValue') },
      { title: 'newValue' as const, value: t('candidate.candidateHeader.tagHistoryNewValue') },
    ],
    [t],
  );

  const tableRows = useMemo(
    () =>
      buildTagChangeHistoryRows(historyRows).map(row => ({
        changedDate: row.changedDate ? moment(row.changedDate).format('MM/DD/YYYY hh:mm A') : '--',
        changedBy: row.changedBy ?? '--',
        oldValue: row.oldValue ?? '--',
        newValue: row.newValue ?? '--',
      })),
    [historyRows],
  );

  return (
    <GenericDialog
      open={open}
      variant="blue"
      classes={{ paper: classes.paperMd }}
      dialogTitleProps={{
        text: t('candidate.candidateHeader.tagHistoryTitle'),
        closeButton: true,
        expandable: true,
      }}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      {loading ? (
        <div className={classes.loader}>
          <CircularProgress size={32} />
        </div>
      ) : tableRows.length === 0 ? (
        <Typography className={classes.emptyState}>{t('candidate.candidateHeader.tagHistoryEmpty')}</Typography>
      ) : (
        <TableContainer elevation={0} component={Paper} className={classes.tableWrap}>
          <Table className={classes.tableContainer}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                {historyColumns.map(header => (
                  <TableCell key={header.title} className={classes.tableHeaderCell}>
                    {header.value}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row, index) => (
                <TableRow key={`${row.changedDate}-${index}`}>
                  {historyColumns.map(header => (
                    <TableCell key={header.title} className={classes.tableCell}>
                      {row[header.title]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </GenericDialog>
  );
};
